import { useState, useEffect } from "react";
import styles from "./CardSelectModal.module.scss";
import closeIcon from "../../../assets/images/referal/Close Icon.svg";
import { BankCardType } from "../../../interface/site.interface";
import Spinner from "../spinner/Spinner";
import GlobalModalComponent from "../globalModal/GlobalModalComponent";

interface CardSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCard: (cardId: number) => void;
  cards: BankCardType[];
  isLoading: boolean;
  amount?: string;
  translationData?: any;
}

const CardSelectModal = ({
  isOpen,
  onClose,
  onSelectCard,
  cards,
  isLoading,
  translationData,
}: CardSelectModalProps) => {
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && cards.length > 0) {
      const defaultCard = cards.find((card) => card.is_default);
      if (defaultCard) {
        setSelectedCardId(defaultCard.id);
      } else {
        setSelectedCardId(cards[0].id);
      }
    }
  }, [isOpen, cards]);

  const handleCardClick = (cardId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCardId(cardId);
  };

  const handleConfirm = () => {
    if (selectedCardId) {
      onSelectCard(selectedCardId);
    }
  };

  const getCardGradient = (cardType: string, index: number) => {
    const gradients = [
      "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
      "linear-gradient(135deg, #2d3436 0%, #636e72 50%, #b2bec3 100%)",
      "linear-gradient(135deg, #0a3d62 0%, #3c6382 50%, #60a3bc 100%)",
      "linear-gradient(135deg, #2c3e50 0%, #4a69bd 50%, #6a89cc 100%)",
      "linear-gradient(135deg, #192a56 0%, #273c75 50%, #40739e 100%)",
    ];

    if (cardType?.toLowerCase() === "visa") {
      return gradients[0];
    } else if (cardType?.toLowerCase() === "mastercard") {
      return gradients[1];
    }
    return gradients[index % gradients.length];
  };

  const getCardTypeLogo = (cardType: string) => {
    if (cardType?.toLowerCase() === "visa") {
      return (
        <div className={styles.visa_logo}>
          <span>VISA</span>
        </div>
      );
    } else if (cardType?.toLowerCase() === "mastercard") {
      return (
        <div className={styles.mastercard_logo}>
          <div className={styles.mc_circles}>
            <div className={styles.mc_circle_red}></div>
            <div className={styles.mc_circle_yellow}></div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Find selected card index for stack ordering
  const selectedIndex = cards.findIndex((c) => c.id === selectedCardId);

  return (
    <GlobalModalComponent isOpen={isOpen} onClose={onClose}>
      <div className={styles.modal_content}>
        <div className={styles.modal_header}>
          <h3 className={styles.modal_title}>
            {translationData?.pay_by_card || "Kartla ödə"}
          </h3>
          <button className={styles.close_button} onClick={onClose}>
            <img src={closeIcon} alt="close" />
          </button>
        </div>

        <div className={styles.wallet_container}>
          {cards.map((card, index) => {
            const isSelected = selectedCardId === card.id;
            const isBeforeSelected = selectedIndex > -1 && index < selectedIndex;
            const isAfterSelected = selectedIndex > -1 && index > selectedIndex;

            return (
              <div
                key={card.id}
                className={`${styles.card_wrapper} ${
                  isSelected ? styles.selected_wrapper : ""
                } ${isBeforeSelected ? styles.before_selected : ""} ${
                  isAfterSelected ? styles.after_selected : ""
                }`}
                onClick={(e) => handleCardClick(card.id, e)}
              >
                <div
                  className={`${styles.card_item} ${
                    isSelected ? styles.selected : ""
                  }`}
                  style={{
                    background: getCardGradient(card.card_type, index),
                  }}
                >
                  <div className={styles.card_content}>
                    <div className={styles.card_top_row}>
                      <div className={styles.chip}></div>
                      {getCardTypeLogo(card.card_type)}
                    </div>
                    <div className={styles.card_number}>{card.pan_masked}</div>
                  </div>

                  {isSelected && (
                    <div className={styles.selected_indicator}>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path
                          d="M20 6L9 17l-5-5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button
          className={styles.confirm_button}
          onClick={handleConfirm}
          disabled={!selectedCardId || isLoading}
        >
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            translationData?.auth_pay || "Ödə"
          )}
        </button>
      </div>
    </GlobalModalComponent>
  );
};

export default CardSelectModal;
